import { ThemeProvider } from '@mui/material';
import { useParsedTheme } from 'api/theme';
import React, {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useState
} from 'react';
import { StorageKey, QueryParam } from 'consts/keys';
import { useLocalStorage } from 'usehooks-ts';
import useQueryParams from 'hooks/useQueryParams';
import useEffectOnceWhen from 'hooks/useEffectOnceWhen';
import DescopeThemeFontLoader from './DescopeThemeFontLoader';
import { createMuiTheme } from './createMuiTheme';

interface IThemeCtxValue {
	themeLoaded: boolean;
	setThemeLoaded: React.Dispatch<React.SetStateAction<boolean>>;
	themeLoadError: boolean;
	mode: 'light' | 'dark';
	setMode: React.Dispatch<React.SetStateAction<'light' | 'dark'>>;
	toggleMode: () => void;
}

const ThemeCtx = createContext<IThemeCtxValue | undefined>(undefined);

interface IThemeCtxProviderProps {
	children: React.ReactNode;
	skipLoadTheme?: boolean;
}

export const ThemeCtxProvider: React.FC<IThemeCtxProviderProps> = ({
	children,
	skipLoadTheme = false
}) => {
	const prefersDarkColorScheme = window.matchMedia(
		'(prefers-color-scheme: dark)'
	).matches;

	const [providedTheme, setProvidedTheme] = useQueryParams<string>(
		QueryParam.Theme
	);

	const systemMode = prefersDarkColorScheme ? 'dark' : 'light';
	const initialMode =
		providedTheme === 'dark' || providedTheme === 'light'
			? providedTheme
			: systemMode;

	const [mode, setMode] = useLocalStorage<'light' | 'dark'>(
		StorageKey.ThemeMode,
		initialMode
	);

	useEffectOnceWhen(
		providedTheme === 'dark' || providedTheme === 'light',
		() => {
			setMode(providedTheme as 'light' | 'dark');
			setProvidedTheme(null);
		},
		[setMode, providedTheme, setProvidedTheme]
	);

	const { data: parsedTheme, isError: themeError } =
		useParsedTheme(!skipLoadTheme);

	const [themeLoaded, setThemeLoaded] = useState<boolean>(false);

	useEffect(() => {
		if (themeError) {
			setThemeLoaded(true);
		}
	}, [themeError]);

	const value = useMemo(
		() => ({
			mode,
			setMode,
			toggleMode: () => setMode(mode === 'light' ? 'dark' : 'light'),
			themeLoaded: skipLoadTheme || themeLoaded,
			setThemeLoaded,
			themeLoadError: themeError
		}),
		[mode, setMode, themeError, themeLoaded, skipLoadTheme]
	);

	const muiTheme = useMemo(
		() =>
			parsedTheme ? createMuiTheme(parsedTheme) : createMuiTheme(undefined),
		[parsedTheme]
	);

	return (
		<ThemeCtx.Provider value={value}>
			<ThemeProvider theme={mode === 'light' ? muiTheme.light : muiTheme.dark}>
				{parsedTheme && (
					<DescopeThemeFontLoader
						theme={parsedTheme}
						setThemeLoaded={setThemeLoaded}
					/>
				)}
				{children}
			</ThemeProvider>
		</ThemeCtx.Provider>
	);
};

export const useThemeCtx = (): IThemeCtxValue => {
	const context = useContext(ThemeCtx);
	if (!context) throw new Error('ThemeCtxProvider is missing');
	return context;
};
